import React, { useState, useEffect, useRef } from 'react';
import {
    Search, MoreVertical, ArrowLeft, Send, Image as ImageIcon,
    Mic, Plus, Phone, Video, Info, CheckCircle, AlertTriangle, X
} from 'lucide-react';

// --- MOCK DATA & CONSTANTS ---

const MOCK_CONVERSATIONS = [
    {
        id: 'mom',
        sender: 'Mom',
        avatar: 'https://ui-avatars.com/api/?name=Mom&background=FF4081&color=fff',
        lastMessage: 'Call me when you get home.',
        time: '10:30 AM',
        unread: false
    },
    {
        id: 'bank_promo',
        sender: 'BankAlerts',
        avatar: 'https://ui-avatars.com/api/?name=Bank&background=004D40&color=fff',
        lastMessage: 'Your statement is ready to view.',
        time: 'Yesterday',
        unread: false
    },
    {
        id: 'pizza_place',
        sender: 'PizzaJoe',
        avatar: 'https://ui-avatars.com/api/?name=Pizza&background=FF9800&color=fff',
        lastMessage: 'Your order is out for delivery! 🍕',
        time: 'Tue',
        unread: false
    }
];

// Default scenario fallback (Smishing)
const getDefaultScenario = () => ({
    title: 'Package Delivery Scam',
    description: "You receive a text about a missed delivery.",
    initialSceneId: 'start',
    scenes: {
        'start': {
            speaker: 'US-Post-Service',
            line: "USPS: We attempted to deliver your package #US9214 but no one was home. Schedule redelivery here: http://usps-redeliver-track.com/help",
            choices: [
                { text: "Click the link to reschedule", nextSceneId: 'fail_click' },
                { text: "Ignore it, I didn't order anything", nextSceneId: 'correct_ignore' },
                { text: "Reply 'STOP'", nextSceneId: 'fail_reply' }
            ]
        },
        'fail_click': {
            speaker: 'System',
            line: "You clicked the link. It led to a fake login page asking for your credit card to pay a 'redelivery fee'.",
            isEndState: true,
            isCorrect: false,
            explanation: "Never click links in unexpected delivery texts. Scammers use these to steal personal info or credit card details.",
            indicators: ["Suspicious URL (usps-redeliver-track.com)", "Urgency", "Unexpected package"]
        },
        'fail_reply': {
            speaker: 'System',
            line: "You replied. This confirms your number is active, leading to more spam calls and texts.",
            isEndState: true,
            isCorrect: false,
            explanation: "Replying to spam, even with 'STOP', often just confirms to scammers that a real person owns the number.",
            indicators: ["Unsolicited text"]
        },
        'correct_ignore': {
            speaker: 'System',
            line: "You ignored the message. Safe choice.",
            isEndState: true,
            isCorrect: true,
            explanation: "Most 'missed delivery' texts are scams. If you are actually expecting a package, check the official site using your tracking number.",
            indicators: ["Generic message", "Suspicious link"]
        }
    }
});

const MessageSimulator = ({ scenario: propScenario, onComplete, challenge }) => {
    const [scenario, setScenario] = useState(null);
    const [history, setHistory] = useState([]);
    const [currentScene, setCurrentScene] = useState(null);
    const [isComplete, setIsComplete] = useState(false);
    const [view, setView] = useState('list'); // 'list' or 'chat'
    const [finalFeedback, setFinalFeedback] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [attackerId, setAttackerId] = useState('attacker_1');

    const chatEndRef = useRef(null);

    // Initialize Scenario
    useEffect(() => {
        let finalScenario = null;
        if (challenge && challenge.steps && challenge.steps.length > 0) {
            // Logic to parse challenge steps if provided (similar to InstagramSimulator)
            // For now, we'll assume the propScenario or default is used for simplicity
            // or adapt the parsing logic if needed.
            const assembledScenes = {};
            let firstSceneId = null;
            const sortedSteps = [...challenge.steps].sort((a, b) => a.order_index - b.order_index);

            sortedSteps.forEach((step, index) => {
                let content = step.content;
                try { if (typeof content === 'string') content = JSON.parse(content); } catch (e) { }
                const sceneKey = content.sceneId || step.title || step.step_id;
                if (index === 0) firstSceneId = sceneKey;
                assembledScenes[sceneKey] = { ...content, choices: content.choices || [] };
            });

            finalScenario = {
                title: challenge.title,
                initialSceneId: firstSceneId,
                scenes: assembledScenes
            };
        } else if (propScenario && propScenario.scenes) {
            finalScenario = propScenario;
        } else {
            finalScenario = getDefaultScenario();
        }

        setScenario(finalScenario);
        const startID = finalScenario?.initialSceneId;
        if (finalScenario && finalScenario.scenes[startID]) {
            setCurrentScene(finalScenario.scenes[startID]);
        }
        setHistory([]);
        setIsComplete(false);
        setAttackerId(`attacker_${Date.now()}`); // Unique ID for this session
    }, [challenge, propScenario]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [history, view]);

    const handleChatSelect = () => {
        setView('chat');
        if (history.length === 0 && currentScene) {
            // Delay the first message slightly for realism
            setTimeout(() => {
                setHistory([{ type: 'attacker', speaker: currentScene.speaker, line: currentScene.line }]);
            }, 500);
        }
    };

    const handleChoice = (choice) => {
        if (isComplete) return;

        setHistory(h => [...h, { type: 'user', speaker: 'You', line: choice.text }]);

        const nextScene = scenario.scenes[choice.nextSceneId];
        if (nextScene) {
            setTimeout(() => {
                // If it's an end state, DO NOT add to history, show popup instead
                if (nextScene.isEndState) {
                    setCurrentScene(nextScene);
                    const feedback = {
                        isCorrect: nextScene.isCorrect,
                        explanation: nextScene.explanation,
                        indicators: nextScene.indicators || []
                    };
                    setFinalFeedback(feedback);
                    setShowFeedbackModal(true);
                } else {
                    // Normal flow
                    setHistory(h => [...h, { type: 'attacker', speaker: nextScene.speaker, line: nextScene.line }]);
                    setCurrentScene(nextScene);
                }
            }, 1000);
        }
    };

    const handleCloseFeedbackModal = () => {
        setShowFeedbackModal(false);
        setIsComplete(true);
        if (onComplete && currentScene) {
            onComplete({
                score: currentScene.isCorrect ? 100 : 0,
                passed: currentScene.isCorrect,
                decisions: { final: "Simulation Ended" }
            });
        }
    };

    // --- RENDERERS ---

    const renderHeader = () => (
        <div className="bg-white px-4 py-3 flex items-center justify-between shadow-sm z-10 sticky top-0">
            {view === 'chat' ? (
                <div className="flex items-center gap-3">
                    <ArrowLeft size={24} className="text-gray-600 cursor-pointer" onClick={() => setView('list')} />
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                            {currentScene?.speaker?.[0] || '?'}
                        </div>
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-900 text-sm">{currentScene?.speaker || 'Unknown'}</span>
                            <span className="text-xs text-gray-500">Mobile</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1 relative">
                    <div className="bg-gray-100 rounded-full h-10 flex items-center px-4 gap-2">
                        <Search size={18} className="text-gray-500" />
                        <span className="text-gray-500 text-sm">Search messages</span>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-4 ml-4">
                {view === 'chat' && <Phone size={22} className="text-gray-600" />}
                <MoreVertical size={22} className="text-gray-600" />
            </div>
        </div>
    );

    const renderList = () => (
        <div className="flex-1 overflow-auto bg-white">
            {/* Attacker Conversation */}
            <div
                onClick={handleChatSelect}
                className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-l-4 border-blue-500"
            >
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg flex-shrink-0">
                    {currentScene?.speaker?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline mb-1">
                        <span className="font-bold text-gray-900 truncate">{currentScene?.speaker || 'Unknown'}</span>
                        <span className="text-xs text-blue-600 font-semibold">Now</span>
                    </div>
                    <p className={`text-sm truncate ${history.length > 0 ? 'text-gray-500' : 'text-gray-900 font-semibold'}`}>
                        {history.length > 0 ? history[history.length - 1].line : (currentScene?.line || "New Message")}
                    </p>
                </div>
            </div>

            {/* Mock Conversations */}
            {MOCK_CONVERSATIONS.map((conv) => (
                <div key={conv.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                        <img src={conv.avatar} alt={conv.sender} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline mb-1">
                            <span className="font-semibold text-gray-900 truncate">{conv.sender}</span>
                            <span className="text-xs text-gray-500">{conv.time}</span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{conv.lastMessage}</p>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderChat = () => (
        <div className="flex flex-col h-full bg-white">
            {/* Scrollable Messages Area */}
            <div className="flex-1 overflow-y-auto px-4 pt-4 pb-20">  {/* ← pb-20 = space for input */}
                <div className="text-center text-xs text-gray-400 my-4">
                    Texting with {currentScene?.speaker || 'Unknown'} (SMS/MMS)
                </div>

                {history.map((msg, idx) => {
                    const isUser = msg.type === 'user';
                    return (
                        <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
                            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${isUser
                                ? 'bg-blue-600 text-white rounded-br-none'
                                : 'bg-gray-200 text-gray-900 rounded-bl-none'
                                }`}>
                                {msg.line}
                            </div>
                        </div>
                    );
                })}

                {/* Choice Buttons – stick near bottom of scrollable area */}
                {!isComplete && currentScene?.choices && (
                    <div className="flex flex-wrap gap-3 justify-center my-6 pb-4">
                        {currentScene.choices.map((choice, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleChoice(choice)}
                                className="bg-white border-2 border-blue-500 text-blue-600 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-50 active:scale-95 transition-all"
                            >
                                {choice.text}
                            </button>
                        ))}
                    </div>
                )}

                <div ref={chatEndRef} />
            </div>

            {/* Fixed Input Bar at Bottom */}
            <div className="shrink-0 p-3 bg-white border-t border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                        <Plus size={20} />
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-11 flex items-center px-4 text-gray-600 text-sm">
                        Text message
                    </div>
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                        <Mic size={20} />
                    </div>
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                        <ImageIcon size={20} />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderFeedbackModal = () => {
        if (!showFeedbackModal || !finalFeedback) return null;
        const { isCorrect, explanation, indicators } = finalFeedback;

        return (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-2xl max-w-xs w-full p-6 m-4 animate-fade-in-up text-gray-900">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            {isCorrect ? <CheckCircle size={28} className="text-green-600" /> : <AlertTriangle size={28} className="text-red-600" />}
                            <h2 className={`text-xl font-bold ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                {isCorrect ? 'Safe Choice' : 'Scam Alert'}
                            </h2>
                        </div>
                        <button onClick={handleCloseFeedbackModal} className="text-gray-400 hover:text-gray-600">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="text-gray-700 space-y-4 text-sm">
                        <p>{explanation}</p>

                        {indicators && indicators.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <h3 className="font-bold text-yellow-700 flex items-center mb-2">
                                    <AlertTriangle size={14} className="mr-2" />
                                    Red Flags:
                                </h3>
                                <ul className="list-disc list-inside space-y-1 text-gray-600 pl-1">
                                    {indicators.map((indicator, index) => <li key={index}>{indicator}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={handleCloseFeedbackModal}
                            className="w-full py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col min-h-[100dvh] bg-white font-sans relative">
            {renderHeader()}
            <div className="flex-1 overflow-hidden">
                {view === 'list' ? renderList() : renderChat()}
            </div>
            {renderFeedbackModal()}
        </div>
    );
};

export default MessageSimulator;